import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Store,
  Building2,
  Mail,
  Phone,
  MapPin,
  FileText,
  ArrowRight,
  CheckCircle,
  Clock,
  Shield,
  TrendingUp,
  Package,
  Users,
  Sparkles
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SellerRegister() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [existingSeller, setExistingSeller] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  
  const [formData, setFormData] = useState({
    company_name: "",
    org_number: "",
    contact_email: user?.email || "",
    contact_phone: "",
    description: "",
    is_company: true,
    address: {
      street: "",
      city: "",
      postal_code: "",
      country: "Norge"
    }
  });

  useEffect(() => {
    checkSellerStatus();
  }, []);

  const checkSellerStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/sellers/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingSeller(res.data);
    } catch (err) {
      // Not a seller yet - that's fine
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.contact_email || !formData.contact_phone) {
      toast.error("Vennligst fyll ut alle obligatoriske felter");
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/sellers/register`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Registrering sendt! Vi behandler søknaden din.");
      setExistingSeller({ ...formData, status: "pending", seller_code: res.data.seller_code });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke registrere. Prøv igjen.");
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Already registered
  if (existingSeller) {
    return (
      <div className="min-h-screen bg-[#050505] py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader className="text-center">
              {existingSeller.status === "pending" ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
                    <Clock className="w-8 h-8 text-yellow-400" />
                  </div>
                  <CardTitle className="text-white text-2xl">Søknad Under Behandling</CardTitle>
                  <CardDescription className="text-[#94A3B8]">
                    Din selgerregistrering venter på godkjenning
                  </CardDescription>
                </>
              ) : existingSeller.status === "approved" ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <CardTitle className="text-white text-2xl">Du er Godkjent Selger!</CardTitle>
                  <CardDescription className="text-[#94A3B8]">
                    Velkommen til AllStillas Marketplace
                  </CardDescription>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                    <Shield className="w-8 h-8 text-red-400" />
                  </div>
                  <CardTitle className="text-white text-2xl">Søknad Avslått</CardTitle>
                  <CardDescription className="text-[#94A3B8]">
                    {existingSeller.rejection_reason || "Ta kontakt med support for mer informasjon"}
                  </CardDescription>
                </>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-white/5 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[#94A3B8]">Selgerkode</span>
                  <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] font-mono">
                    {existingSeller.seller_code}
                  </Badge>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[#94A3B8]">Firmanavn</span>
                  <span className="text-white font-medium">{existingSeller.company_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#94A3B8]">Status</span>
                  <Badge className={
                    existingSeller.status === "approved" ? "bg-green-500/20 text-green-400" :
                    existingSeller.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-red-500/20 text-red-400"
                  }>
                    {existingSeller.status === "approved" ? "Godkjent" :
                     existingSeller.status === "pending" ? "Venter" : "Avslått"}
                  </Badge>
                </div>
              </div>

              {existingSeller.status === "approved" && (
                <div className="space-y-3">
                  <Link to="/seller/dashboard">
                    <Button className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">
                      Gå til Selger Dashboard
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/seller/products">
                    <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                      Administrer Produkter
                    </Button>
                  </Link>
                </div>
              )}

              {existingSeller.status === "pending" && (
                <div className="text-center text-[#94A3B8] text-sm">
                  <p>Vi behandler søknader innen 1-2 virkedager.</p>
                  <p>Du får beskjed på e-post når søknaden er behandlet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7931A]/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-[#F7931A]" />
            <span className="text-[#F7931A] text-sm font-medium">AllStillas Marketplace</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-['Unbounded'] font-bold text-white mb-4">
            Bli Selger på AllStillas
          </h1>
          <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
            Nå tusenvis av kunder i stillasmarkedet. Selg produktene dine på Norges største 
            stillasplattform - helt gratis opp til 50.000 NOK i salg!
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-green-500/20 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">Gratis Oppstart</h3>
              <p className="text-[#94A3B8] text-sm">
                0% provisjon på dine første 50.000 NOK i salg
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">Stort Kundenettwerk</h3>
              <p className="text-[#94A3B8] text-sm">
                Nå profesjonelle kunder og privatpersoner over hele Norge
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-purple-500/20 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">Enkelt Oppsett</h3>
              <p className="text-[#94A3B8] text-sm">
                Last opp produkter og begynn å selge på minutter
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Registration Form */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Store className="w-5 h-5 text-[#23F7DD]" />
              Registrer Som Selger
            </CardTitle>
            <CardDescription className="text-[#94A3B8]">
              Fyll ut skjemaet under for å søke om å bli selger på AllStillas Marketplace
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Type Toggle */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_company: true }))}
                  className={`flex-1 p-4 rounded-lg border transition-all ${
                    formData.is_company 
                      ? "bg-[#23F7DD]/10 border-[#23F7DD] text-white" 
                      : "border-white/20 text-[#94A3B8] hover:border-white/40"
                  }`}
                >
                  <Building2 className="w-6 h-6 mx-auto mb-2" />
                  <span className="block font-medium">Bedrift</span>
                  <span className="text-xs opacity-70">Med org.nummer</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_company: false }))}
                  className={`flex-1 p-4 rounded-lg border transition-all ${
                    !formData.is_company 
                      ? "bg-[#23F7DD]/10 border-[#23F7DD] text-white" 
                      : "border-white/20 text-[#94A3B8] hover:border-white/40"
                  }`}
                >
                  <Users className="w-6 h-6 mx-auto mb-2" />
                  <span className="block font-medium">Privatperson</span>
                  <span className="text-xs opacity-70">Enkeltperson</span>
                </button>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">
                    {formData.is_company ? "Firmanavn" : "Fullt Navn"} *
                  </label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder={formData.is_company ? "Stillas Norge AS" : "Ola Nordmann"}
                    className="bg-black/50 border-white/10 text-white"
                    required
                  />
                </div>
                {formData.is_company && (
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Organisasjonsnummer</label>
                    <Input
                      value={formData.org_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, org_number: e.target.value }))}
                      placeholder="123 456 789"
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-1">
                    <Mail className="w-3 h-3" /> E-post *
                  </label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                    placeholder="kontakt@firma.no"
                    className="bg-black/50 border-white/10 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Telefon *
                  </label>
                  <Input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                    placeholder="+47 123 45 678"
                    className="bg-black/50 border-white/10 text-white"
                    required
                  />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4">
                <label className="text-sm text-[#94A3B8] flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Adresse
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    value={formData.address.street}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, street: e.target.value }
                    }))}
                    placeholder="Gateadresse"
                    className="bg-black/50 border-white/10 text-white"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={formData.address.postal_code}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, postal_code: e.target.value }
                      }))}
                      placeholder="Postnr"
                      className="bg-black/50 border-white/10 text-white"
                    />
                    <Input
                      value={formData.address.city}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, city: e.target.value }
                      }))}
                      placeholder="Sted"
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Om {formData.is_company ? "Bedriften" : "Deg"}
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Beskriv hva du selger og din erfaring i bransjen..."
                  className="bg-black/50 border-white/10 text-white"
                  rows={4}
                />
              </div>

              {/* Terms */}
              <div className="p-4 bg-white/5 rounded-lg text-sm text-[#94A3B8]">
                <p className="mb-2">Ved å registrere deg godtar du:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>AllStillas Marketplace sine <a href="#" className="text-[#23F7DD] hover:underline">vilkår og betingelser</a></li>
                  <li>0% provisjon opp til 50.000 NOK, deretter 8% av salg</li>
                  <li>At søknaden behandles manuelt (1-2 virkedager)</li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#F7931A] to-[#FFCD00] text-black font-bold py-6 text-lg hover:opacity-90"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                    Sender...
                  </>
                ) : (
                  <>
                    Send Søknad
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
